import { UserContext } from "context/user";
import { gql, useMutation, useQuery } from "@apollo/client";
import { useContext, useEffect, useState, useCallback, useMemo } from "react";

// Components
import { Card, Grid, Icon, Menu, MenuItem } from "@mui/material";
import Footer from "components/Footer";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import DataTable from "components/Tables/DataTable";
import SoftButton from "components/SoftButton";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import ModalDelete from "components/Modals/Delete";

import { MessageManager } from "context";
import { getColor } from "utils/colors";
import ModalTasas from "./components/ModalTasas";
import dataTasas from "./data/dataTasas";

// Hook de debounce
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

function Tasas() {
  const [rows, setRows] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounce(searchInput, 500);

  const [openMenu, setOpenMenu] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openModalAdd, setOpenModalAdd] = useState(false);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [selectedTasa, setSelectedTasa] = useState(null);
  // const [page, setPage] = useState(1);
  // const [pagesInfo, setPagesInfo] = useState({ pages: 0, total: 0 });
  const [orderBy, setOrderBy] = useState({ campo: "id", orden: "ASC" });

  const { verificarAcceso } = useContext(UserContext);
  const { handleSnackbar } = useContext(MessageManager);
  const { columns, getRows } = dataTasas;

  // Funciones con useCallback
  const handleOrderBy = useCallback((campo) => {
    setOrderBy((prev) => ({
      campo,
      orden: prev.campo === campo && prev.orden === "ASC" ? "DESC" : "ASC",
    }));
  }, []);

  const handleOpenMenu = useCallback((event, item) => {
    setAnchorEl(event.currentTarget);
    setOpenMenu(true);
    setSelectedTasa(item);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setAnchorEl(null);
    setOpenMenu(false);
  }, []);

  const handleSearch = (event) => {
    setSearchInput(event.target.value);
  };

  const [deleteTasa] = useMutation(
    gql`
      mutation deleteTasa($id: ID!) {
        deleteTasa(id: $id)
      }
    `
  );

  const { loading, error, data, refetch } = useQuery(
    gql`
      query getTasas($search: String, $order: Order) {
        paginationInfo {
          pages
          total
        }
        tasas(search: $search, order: $order) {
          id
          categoria
          importe
          porcentaje
          # anio
        }
      }
    `,
    {
      variables: {
        search: search || null,
        order: { field: orderBy.campo, order: orderBy.orden },
      },
      fetchPolicy: "no-cache",
    }
  );

  const memoizedRows = useMemo(() => {
    if (data) {
      return getRows(data.tasas, handleOpenMenu);
    }
    return [];
  }, [data, getRows, handleOpenMenu]);

  const handleDeleteTasa = async (id) => {
    try {
      await deleteTasa({ variables: { id } });
      refetch();
      handleSnackbar("Tasa eliminada correctamente", "success");
    } catch (e) {
      handleSnackbar(e.message || "Error al eliminar la tasa", "error");
    }
  };

  // Render del menú contextual
  const renderMenu = (
    <Menu id="simple-menu" anchorEl={anchorEl} open={Boolean(openMenu)} onClose={handleCloseMenu}>
      <MenuItem disabled={!verificarAcceso(262)}
        onClick={() => {
          setOpenModalAdd(true);
          handleCloseMenu();
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <SoftTypography variant="p">Editar</SoftTypography>
          <Icon fontSize="small" sx={{ verticalAlign: "middle", color: getColor("gobSecondary") }}>
            edit
          </Icon>
        </SoftBox>
      </MenuItem>
      {/* <MenuItem
        onClick={() => {
          setOpenModalDelete(true);
          handleCloseMenu();
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: "100%" }}
        >
          <SoftTypography variant="p">Eliminar</SoftTypography>
          <Icon fontSize="small" color="error">
            delete
          </Icon>
        </SoftBox>
      </MenuItem> */}
    </Menu>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox>
          <Card>
            <Grid container spacing={2} p={3}>
              <Grid item xs={12}>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                  <SoftTypography variant="h6" fontWeight="bold">
                    Tasas
                  </SoftTypography>
                  <SoftBox display="flex" alignItems="center" gap={2}>
                    <SoftInput
                      placeholder="Buscar..."
                      icon={{ component: "search", direction: "left" }}
                      onChange={handleSearch}
                      value={searchInput}
                    />
                    {/* <SoftButton
                      color="primary"
                      onClick={() => {
                        setSelectedTasa(null);
                        setOpenModalAdd(true);
                      }}
                      startIcon={<Icon>add</Icon>}
                    >
                      Nueva
                    </SoftButton> */}
                  </SoftBox>
                </SoftBox>
              </Grid>
            </Grid>
            <SoftBox>
              {loading ? (
                <Loading />
              ) : memoizedRows?.length > 0 ? (
                <DataTable
                  columns={columns}
                  rows={memoizedRows}
                  order={orderBy}
                  setOrder={handleOrderBy}
                />
              ) : (
                <SinDatos />
              )}
            </SoftBox>
            {/* <CustomPagination
              loading={loading}
              length={rows.length}
              total={pagesInfo.total}
              pages={pagesInfo.pages}
              page={page}
              setPage={setPage}
              noCard
            /> */}
          </Card>
          {renderMenu}
          <ModalTasas
            open={openModalAdd}
            handleClose={() => setOpenModalAdd(false)}
            refetch={refetch}
            idTasa={selectedTasa?.id}
          />
          <ModalDelete
            open={openModalDelete}
            handleClose={() => setOpenModalDelete(false)}
            nombre={`la tasa de categoría ${selectedTasa?.categoria}`}
            functionDelete={() => {
              handleDeleteTasa(selectedTasa.id);
              setOpenModalDelete(false);
            }}
          />
        </SoftBox>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Tasas;
